/**
 * Classification and user-facing copy for API errors raised by the survey
 * management page (create / clone / delete). Kept apart from the page so the
 * component stays focused on orchestration.
 */

export type ApiError = Error & {
  status?: number;
  code?: string;
  details?: Array<{ code?: string; message?: string }>;
};

const DUPLICATE_SURVEY_CONFLICT_MESSAGE =
  "A survey for this edition and publication already exists.";

const SURVEY_DELETE_BLOCKED_MESSAGE =
  "This survey cannot be deleted because referees have already submitted responses to it.";

const SURVEY_DELETE_BLOCKED_REASON = "Deleting it is blocked to protect collected data.";

/**
 * The api client falls back to "HTTP <status>" when the response body carries
 * no `error` string — that is not a message worth showing to an operator.
 */
function informativeApiMessage(error: unknown): string | null {
  if (!(error instanceof Error)) return null;
  const message = error.message.trim();
  if (message.length === 0 || /^HTTP \d+$/.test(message)) return null;
  return message;
}

function endsWithPunctuation(text: string): boolean {
  return /[.!?]$/.test(text);
}

// --- create / clone: duplicate edition + publication (+ conditions) ----------

export function isDuplicateSurveyConflict(error: unknown): boolean {
  const err = error as ApiError | undefined;
  if (!err) return false;

  if (err.status === 409) return true;

  if (err.code?.toUpperCase().includes("CONFLICT")) return true;
  if (err.code?.toUpperCase().includes("DUPLICATE")) return true;

  if (err.details?.some?.(detail => detail.code?.toUpperCase().includes("DUPLICATE"))) {
    return true;
  }

  return false;
}

export function duplicateSurveyConflictMessage(error: unknown): string {
  // Prefer the API's message (it names the conflicting conditions); fall back
  // to the generic copy when the client only produced "HTTP <status>".
  return informativeApiMessage(error) ?? DUPLICATE_SURVEY_CONFLICT_MESSAGE;
}

// --- delete: survey already has referee responses ---------------------------

/**
 * `DELETE /v1/survey/surveys/:id` answers 409 CONFLICT when the survey already
 * has referee responses. This is a deliberate guard, not a failure, so the
 * page must present it as such rather than as a generic error.
 */
export function isSurveyDeleteConflict(error: unknown): boolean {
  const err = error as ApiError | undefined;
  if (!err) return false;

  if (err.status === 409) return true;
  if (err.code?.toUpperCase() === "CONFLICT") return true;

  return false;
}

/**
 * Builds the inline copy for a blocked delete. The API message is kept when it
 * is informative, and the "by design" sentence is always appended so the
 * operator understands the block is intentional.
 */
export function surveyDeleteBlockedMessage(error: unknown): string {
  const lead = informativeApiMessage(error) ?? SURVEY_DELETE_BLOCKED_MESSAGE;
  const sentence = endsWithPunctuation(lead) ? lead : `${lead}.`;
  return `${sentence} ${SURVEY_DELETE_BLOCKED_REASON}`;
}
